import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Database, BrainCircuit, ShieldCheck, Blocks, RefreshCw } from "lucide-react";
import { fetchSgLayers, fetchSgStrategyModulesSummary, enableSgStrategyModule, disableSgStrategyModule, updateSgStrategies, updateSgBotConfig } from "@/lib/smartgrid_api";
import { StrategyDashboard } from "@/components/StrategyDashboard";
import { toast } from "sonner";

const C = {
    bg: "#0B0F14", panel: "#0F1620", panel2: "#121C28", border: "#1B2A3A",
    text: "#E6EDF6", sub: "#A7B4C6", dim: "#6F7D92",
    bull: "#2BE38B", bear: "#FF5C6C", cyan: "#2EE6D6", blue: "#3DA7FF",
    warn: "#FFB020",
};

const fmt = (v, d = 2) => (v === null || v === undefined || Number.isNaN(Number(v))
    ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));
const money = (v) => (v === null || v === undefined ? "—"
    : `${Number(v) >= 0 ? "+" : ""}$${Number(v).toFixed(2)}`);
const pnlColor = (v) => (v === null || v === undefined ? C.dim
    : Number(v) >= 0 ? C.bull : C.bear);

const SectionHeader = ({ icon: Icon, kicker, title, right }) => (
    <div className="flex items-center gap-2.5 mb-3">
        <span className="p-1.5 rounded-md" style={{ background: C.panel2, border: `1px solid ${C.border}` }}>
            <Icon size={14} style={{ color: C.cyan }} />
        </span>
        <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: C.dim }}>{kicker}</div>
            <div className="text-[14px] font-medium" style={{ color: C.text }}>{title}</div>
        </div>
        <div className="flex-1" />
        {right}
    </div>
);

const Tile = ({ label, value, color, mono = true }) => (
    <div className="rounded-lg px-3 py-2" style={{ background: C.panel2, border: `1px solid ${C.border}` }}>
        <div className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>{label}</div>
        <div className={`${mono ? "font-mono tabular-nums" : ""} text-[14px]`} style={{ color: color || C.text }}>
            {value}
        </div>
    </div>
);

const Flag = ({ label, on, danger = false }) => (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] uppercase tracking-[0.1em]"
        style={{
            color: on ? (danger ? C.bear : C.bull) : C.dim,
            border: `1px solid ${on ? (danger ? "rgba(255,92,108,0.4)" : "rgba(43,227,139,0.35)") : C.border}`,
            background: on ? (danger ? "rgba(255,92,108,0.08)" : "rgba(43,227,139,0.06)") : "transparent",
        }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: on ? (danger ? C.bear : C.bull) : "#3A4759" }} />
        {label}
    </span>
);

const StrategyCard = ({ m, onOpen, onToggle }) => {
    const s = m.stats || null;
    const enabled = m.enabled;
    return (
        <div className="rounded-xl p-3 cursor-pointer btn-anim"
            style={{
                background: C.panel, border: `1px solid ${enabled ? C.border : "#141d29"}`,
                opacity: enabled || m.status === "planned" ? 1 : 0.75,
            }}
            onClick={() => onOpen(m.id)}
            data-testid={`strategy-card-${m.id}`}>
            <div className="flex items-center gap-2 mb-2">
                <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: m.color }} />
                <span className="text-[13px] font-medium truncate" style={{ color: C.text }}>{m.name}</span>
                <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.1em] shrink-0"
                    style={{ color: C.dim, border: `1px solid ${C.border}` }}>
                    {m.status}
                </span>
                {m.effective_mode ? (
                    <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.1em] shrink-0"
                        style={{
                            color: m.effective_mode === "chase" ? C.bull : C.dim,
                            border: `1px solid ${m.effective_mode === "chase" ? "rgba(43,227,139,0.35)" : C.border}`,
                        }}
                        data-testid={`strategy-card-mode-${m.id}`}>
                        {m.effective_mode}
                    </span>
                ) : null}
                <div className="flex-1" />
                {m.locked ? (
                    <span className="text-[9px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>locked</span>
                ) : (
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggle(m.id, !enabled); }}
                        className="btn-anim px-2 h-6 rounded text-[10px] font-mono"
                        style={{
                            color: enabled ? C.bull : C.dim,
                            border: `1px solid ${enabled ? "rgba(43,227,139,0.35)" : C.border}`,
                        }}
                        data-testid={`strategy-card-toggle-${m.id}`}>
                        {enabled ? "ON" : "OFF"}
                    </button>
                )}
            </div>
            <div className="grid grid-cols-3 gap-1.5 font-mono tabular-nums text-[12px]">
                <div>
                    <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>Net PnL</div>
                    <div style={{ color: pnlColor(s?.net_pnl) }}>{money(s?.net_pnl)}</div>
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>Win</div>
                    <div style={{ color: C.sub }}>{s?.win_rate_pct === undefined || s?.win_rate_pct === null ? "—" : `${fmt(s.win_rate_pct, 0)}%`}</div>
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>PF</div>
                    <div style={{ color: C.sub }}>{fmt(s?.profit_factor)}</div>
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>Sharpe</div>
                    <div style={{ color: C.sub }}>{fmt(s?.sharpe, 2)}</div>
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>Max DD</div>
                    <div style={{ color: s?.max_drawdown_pct < 0 ? C.bear : C.sub }}>
                        {s?.max_drawdown_pct === undefined || s?.max_drawdown_pct === null ? "—" : `${fmt(s.max_drawdown_pct, 1)}%`}
                    </div>
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>Cycles</div>
                    <div style={{ color: C.sub }}>{fmt(s?.cycles, 0)}</div>
                </div>
            </div>
            {enabled && m.blocked_reason ? (
                <div className="mt-2 text-[10px] leading-snug truncate" title={m.blocked_reason}
                    style={{ color: C.dim }}
                    data-testid={`strategy-card-blocked-${m.id}`}>
                    idle: {m.blocked_reason}
                </div>
            ) : null}
            {m.last_error ? (
                <div className="mt-1 text-[10px] leading-snug truncate"
                    title={`${m.last_error.site || ""} (x${m.last_error.count || 1}): ${m.last_error.error || ""}`}
                    style={{ color: C.bear }}
                    data-testid={`strategy-card-error-${m.id}`}>
                    last error: {m.last_error.error}
                </div>
            ) : null}
        </div>
    );
};

/**
 * Smart Grid Architecture — the four-layer view of the system:
 * L0 Data → L1 Prediction → L2 Controls → L3 Strategies, with per-strategy
 * dashboards (click a card) and per-strategy layer-input toggles.
 */
export default function SmartGridArchitecturePage({ onBack }) {
    const [layers, setLayers] = useState(null);
    const [summary, setSummary] = useState(null);
    const [openSid, setOpenSid] = useState(null);
    const [err, setErr] = useState("");

    const load = async () => {
        try {
            const [l, s] = await Promise.all([fetchSgLayers(), fetchSgStrategyModulesSummary()]);
            setLayers(l); setSummary(s); setErr("");
        } catch (e) {
            setErr(String(e?.response?.data?.detail || e.message || e));
        }
    };
    useEffect(() => {
        load();
        const t = setInterval(load, 4000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onToggle = async (sid, on) => {
        try {
            const res = on ? await enableSgStrategyModule(sid) : await disableSgStrategyModule(sid);
            toast.success(`${sid} ${res.enabled ? "enabled" : "disabled"}`);
            load();
        } catch (e) {
            toast.error(String(e?.response?.data?.detail || e.message || e));
        }
    };

    // Global protection toggles (supervisor-level gates, e.g. pump_guard,
    // pump_chase_guard_link) — each flips its own endpoint field and persists.
    const onProtectionToggle = async (key, on) => {
        try {
            await updateSgStrategies({ [key]: on });
            toast.success(`${key} ${on ? "enabled" : "disabled"}`);
            load();
        } catch (e) {
            toast.error(String(e?.response?.data?.detail || e.message || e));
        }
    };

    // Config-level LIVE opt-ins (knife-catch / pump-chase / dalton on real
    // funds) — bot-config fields, persisted via PUT /bot/config.
    const onLiveGateToggle = async (key, on) => {
        if (on && !window.confirm(`${key} allows REAL-FUNDS orders when the bot trades LIVE. Enable?`)) return;
        try {
            await updateSgBotConfig({ [key]: on });
            toast.success(`${key} ${on ? "enabled" : "disabled"}`);
            load();
        } catch (e) {
            toast.error(String(e?.response?.data?.detail || e.message || e));
        }
    };

    const l0 = layers?.layer0_data, l1 = layers?.layer1_prediction,
        l2 = layers?.layer2_controls, l3 = layers?.layer3_strategies;
    const modules = summary?.items || l3?.modules || [];
    const openMeta = useMemo(() => modules.find((m) => m.id === openSid), [modules, openSid]);
    const horizons = l1?.horizons || {};
    const exceptions = l2?.exception_counters || {};
    const integrity = summary?.integrity || l3?.integrity || {};

    return (
        <div className="min-h-screen" style={{ background: C.bg, color: C.text }}>
            <div className="sticky top-0 z-40 border-b" style={{ background: "rgba(11,15,20,0.92)", backdropFilter: "blur(8px)", borderColor: C.border }}>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 h-14 flex items-center gap-3">
                    <button onClick={onBack} className="btn-anim flex items-center gap-1.5 text-sm" style={{ color: C.sub }}
                        data-testid="architecture-back-button">
                        <ArrowLeft size={15} /> SmartGrid
                    </button>
                    <div className="h-5 w-px" style={{ background: C.border }} />
                    <div className="text-[14px] font-medium">Architecture — Layered System</div>
                    <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.12em]"
                        style={{ color: C.cyan, border: `1px solid ${C.border}` }}>
                        L0 · L1 · L2 · L3
                    </span>
                    <div className="flex-1" />
                    {l3?.stats_age_s !== null && l3?.stats_age_s !== undefined && (
                        <span className="flex items-center gap-1.5 text-[11px] font-mono" style={{ color: C.dim }}>
                            <RefreshCw size={11} /> stats {fmt(l3.stats_age_s, 0)}s ago
                        </span>
                    )}
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-5 space-y-5">
                {err && (
                    <div className="panel p-3 text-[12px]" style={{ color: C.bear }} data-testid="architecture-error">
                        {err}
                    </div>
                )}

                {/* ---- Layer 0 · Data ---- */}
                <section className="panel p-4" data-testid="layer-0-section">
                    <SectionHeader icon={Database} kicker="Layer 0" title="Data"
                        right={<Flag label={l0?.feed_status || "…"} on={l0?.feed_status === "CONNECTED"} />} />
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                        <Tile label="Mid price" value={fmt(l0?.mid)} />
                        <Tile label="Feature age" value={l0?.feature_age_s === null || l0?.feature_age_s === undefined ? "—" : `${l0.feature_age_s}s`}
                            color={l0?.feature_age_s > 10 ? C.warn : C.text} />
                        <Tile label="Features" value={fmt(l0?.feature_count, 0)} />
                        <Tile label="Data sufficiency" value={l0?.data_sufficiency === undefined || l0?.data_sufficiency === null ? "—" : fmt(l0.data_sufficiency, 2)} />
                        <Tile label="Binance fast-moves" value={fmt(l0?.binance_leader?.fast_moves, 0)} />
                        <Tile label="Liq bursts" value={fmt(l0?.binance_leader?.liq_bursts, 0)} />
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        <Flag label="binance leader" on={!!l0?.binance_leader?.connected} />
                        <Flag label="recorder" on={!!(l0?.recorder && Object.keys(l0.recorder).length)} />
                        <Flag label="macro klines" on={!!l0?.macro_loaded} />
                        <Flag label="external (F&G/OI)" on={!!(l0?.external && Object.keys(l0.external).length)} />
                    </div>
                </section>

                {/* ---- Layer 1 · Prediction ---- */}
                <section className="panel p-4" data-testid="layer-1-section">
                    <SectionHeader icon={BrainCircuit} kicker="Layer 1" title="Prediction" />
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                        <Tile label="Regime" value={l1?.regime?.regime || "—"} mono={false} color={C.cyan} />
                        <Tile label="Regime conf" value={l1?.regime?.confidence === undefined ? "—" : `${fmt(l1.regime.confidence, 0)}%`} />
                        <Tile label="Composite confidence" value={l1?.confidence === undefined || l1?.confidence === null ? "—" : `${fmt(l1.confidence, 0)}%`} />
                        <Tile label="Autonomy band" value={l1?.autonomy_band || "—"} mono={false} />
                        <Tile label="ML P(up)" value={l1?.online_ml?.p_up === undefined ? "—" : fmt(l1.online_ml.p_up, 3)} />
                        <Tile label="Twins" value={`${fmt(l1?.twins?.count, 0)} · gen ${fmt(l1?.twins?.generation, 0)}`} />
                    </div>
                    {Object.keys(horizons).length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                            {Object.entries(horizons).slice(0, 4).map(([h, v]) => (
                                <div key={h} className="rounded-lg px-3 py-2" style={{ background: C.panel2, border: `1px solid ${C.border}` }}>
                                    <div className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>{h}m horizon</div>
                                    <div className="font-mono tabular-nums text-[12px] flex gap-2">
                                        <span style={{ color: C.bull }}>▲{fmt(v?.bull, 0)}%</span>
                                        <span style={{ color: C.dim }}>≈{fmt(v?.sideways, 0)}%</span>
                                        <span style={{ color: C.bear }}>▼{fmt(v?.bear, 0)}%</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* ---- Layer 2 · Controls ---- */}
                <section className="panel p-4" data-testid="layer-2-section">
                    <SectionHeader icon={ShieldCheck} kicker="Layer 2" title="Controls" />
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        <Flag label="kill switch" on={!!l2?.kill_switch} danger />
                        <Flag label="pause buys" on={!!l2?.pause_buys} danger />
                        <Flag label="breaker" on={!!l2?.breaker_active} danger />
                        <Flag label="fast-move guard" on={!!l2?.guard_active} danger />
                        <Flag label="pump guard" on={!!l2?.pump_guard} />
                        <Flag label={`knife: ${l2?.fall_state?.state || "calm"}`} on={l2?.fall_state?.state === "knife"} danger />
                        <Flag label={`trend: ${l2?.trend_view?.state || "neutral"}`} on={l2?.trend_view?.state !== "neutral"} />
                        <Flag label="guard auto-tune" on={!!l2?.guard_auto_tune} />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                        <Tile label="Buy throttle" value={l2?.buy_throttle === undefined ? "—" : `${Math.round(l2.buy_throttle * 100)}%`} />
                        <Tile label="Risk score" value={fmt(l2?.risk?.risk_score, 0)} />
                        <Tile label="Crash score" value={fmt(l2?.fall_state?.crash_score, 2)} />
                        <Tile label="Meta proposal" value={l2?.meta_controller?.pause ? "pause" : l2?.meta_controller?.spacing_mult ? `spacing ×${fmt(l2.meta_controller.spacing_mult, 2)}` : "—"} mono={false} />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.12em] mb-1.5" style={{ color: C.dim }}>Protection toggles (global — click to flip)</div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {Object.entries(l2?.toggles || {}).map(([k, v]) => (
                            <button key={k} type="button" className="btn-anim cursor-pointer"
                                onClick={() => onProtectionToggle(k, !v)}
                                title={`Click to turn ${k} ${v ? "off" : "on"}`}
                                data-testid={`protection-toggle-${k}`}>
                                <Flag label={k} on={!!v} />
                            </button>
                        ))}
                    </div>
                    {l2?.live_gates && (
                        <>
                            <div className="text-[10px] uppercase tracking-[0.12em] mb-1.5" style={{ color: C.dim }}>
                                LIVE gates (real-funds opt-ins — click to flip)
                            </div>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {Object.entries(l2.live_gates).map(([k, v]) => (
                                    <button key={k} type="button" className="btn-anim cursor-pointer"
                                        onClick={() => onLiveGateToggle(k, !v)}
                                        title={`Click to turn ${k} ${v ? "off" : "on"} (real funds when LIVE)`}
                                        data-testid={`live-gate-toggle-${k}`}>
                                        <Flag label={k.replace(/_enabled$/, "")} on={!!v} danger />
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                    {Object.keys(exceptions).length > 0 && (
                        <>
                            <div className="text-[10px] uppercase tracking-[0.12em] mb-1.5" style={{ color: C.dim }}>
                                Contained exceptions (telemetry)
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {Object.entries(exceptions).slice(0, 8).map(([site, info]) => (
                                    <span key={site} className="px-2 py-1 rounded text-[10px] font-mono"
                                        style={{ color: C.warn, border: `1px solid ${C.border}` }}
                                        title={info?.error || ""}>
                                        {site} ×{info?.count}
                                    </span>
                                ))}
                            </div>
                        </>
                    )}
                </section>

                {/* ---- Layer 3 · Strategies ---- */}
                <section className="panel p-4" data-testid="layer-3-section">
                    <SectionHeader icon={Blocks} kicker="Layer 3" title="Strategies"
                        right={integrity?.attribution_complete !== undefined && (
                            <Flag label={integrity.attribution_complete ? "attribution ✓" : "attribution drift"}
                                on={!!integrity.attribution_complete} danger={!integrity.attribution_complete} />
                        )} />
                    <div className="text-[11px] mb-3" style={{ color: C.dim }}>
                        Independent modules — each consumes Layers 0/1/2 through its own toggleable inputs, never each other.
                        Click a card for the full dashboard.
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {modules.map((m) => (
                            <StrategyCard key={m.id} m={m} onOpen={(sid) => setOpenSid(sid === openSid ? null : sid)} onToggle={onToggle} />
                        ))}
                    </div>
                    {openSid && (
                        <StrategyDashboard sid={openSid} meta={openMeta}
                            onClose={() => setOpenSid(null)} onToggleEnabled={onToggle} />
                    )}
                </section>
            </div>
        </div>
    );
}
